import useAuthenticatedApi from '@/src/Authentication/components/hooks/useAuthenticatedApi';
import useUserOrg from '@/src/Authentication/components/hooks/useUserOrg';

interface RekognitionSettings {
    id: number;
    enabled: boolean;
    type: string;
    confidence: number;
}

export interface RekognitionLabel {
    id: number;
    mediaId: number;
    label: string;
    createdAt: string;
}

export interface RekognitionDetectedFace {
    id: number;
    mediaId: number;
    faceVector: string;
    faceCatalogUrl: string;
}

export default function useRekognition() {
    const authenticatedApi = useAuthenticatedApi();
    const org = useUserOrg();

    const orgId = org?.orgId || 0;

    const createSettings = async (
        activationId: number,
        enabled: boolean,
        type: string,
        confidence: number,
    ) => {
        const response = await authenticatedApi.post<RekognitionSettings>(
            `/${orgId}/rekognition/settings/${activationId}`,
            {
                enabled,
                type,
                confidence,
            },
        );
        return response;
    };

    const updateSettings = async (
        settingsId: number,
        enabled: boolean,
        type: string,
        confidence: number,
    ) => {
        const response = await authenticatedApi.put<RekognitionSettings>(
            `/${orgId}/rekognition/settings/${settingsId}`,
            {
                enabled,
                type,
                confidence,
            },
        );
        return response;
    };

    const getSettings = async (activationId: number) => {
        const response = await authenticatedApi.get<RekognitionSettings>(
            `/${orgId}/rekognition/settings/${activationId}`,
        );
        return response;
    };

    const getLabels = async (activationId: number) => {
        const response = await authenticatedApi.get<RekognitionLabel[]>(
            `/${orgId}/rekognition/labels/${activationId}`,
        );
        return response;
    };

    const getDetectedFaces = async (mediaId: number) => {
        const response = await authenticatedApi.get<RekognitionDetectedFace[]>(
            `/${orgId}/detected-faces/media/${mediaId}`,
        );
        return response;
    };

    const getDetectedLabels = async (mediaId: number) => {
        const response = await authenticatedApi.get<RekognitionLabel[]>(
            `/${orgId}/rekognition/labels/media/${mediaId}`,
        );
        return response;
    };

    const triggerRekognition = async (
        mediaId: number,
        shareIdentifier: string,
        activationId: number,
    ) => {
        const response = await authenticatedApi.post(
            `/${orgId}/rekognition/trigger/${mediaId}`,
            {
                shareIdentifier,
                activationId,
            },
        );
        return response;
    };

    const runRekognition = async (activationId: number) => {
        const response = await authenticatedApi.post(
            `/${orgId}/rekognition/run/${activationId}`,
        );
        return response;
    };

    return {
        createSettings,
        updateSettings,
        getSettings,
        getLabels,
        triggerRekognition,
        getDetectedFaces,
        getDetectedLabels,
        runRekognition,
    };
}
